import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as S from "../../styles";
import BackNavigationHeader from "../BackNavigationHeader";
import { locale } from "../../locale";
import { useHistory } from "react-router";
import { Fetch } from "../../utilities/Fetch";
import { DateTime } from "luxon";
import "./BannerManagement.css";


interface BannerFormData {
  id: number | null;
  isActive: boolean;
  startDate: string;
  endDate: string;
  bannerMessage: string;
}

const MST_ZONE = "America/Edmonton";

const toMSTDate = (dateStr: string): string => {
  if (!dateStr) return "";
  const dt = DateTime.fromISO(dateStr.replace(' ', 'T')).setZone(MST_ZONE);
  return dt.isValid ? dt.toFormat("yyyy-MM-dd") : "";
};

const BannerManagement: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;

  const [formData, setFormData] = useState<BannerFormData>({
    id: null,
    isActive: false,
    startDate: "",
    endDate: "",
    bannerMessage: "",
  });

  useEffect(() => {
    loadBannerData();
  }, []);

  const loadBannerData = () => {
    Fetch.Build("/toggle", dispatch)
      .success((response: any) => {
        const banner = response.find((x: any) => x.name === "banner");

        if (banner) {
          setFormData({
            id: banner.id,
            isActive: banner.on ?? false,
            startDate: toMSTDate(banner.startDate),
            endDate: toMSTDate(banner.endDate),
            bannerMessage: banner.bannerMessage || "",
          });
        }
      })
      .error((error: any) => {
        console.log(error);
      })
      .get();
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value, checked, type } = e.target as HTMLInputElement;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleClear = async () => {
    await loadBannerData();
  };

  const handleSave = () => {
    const startMST = formData.startDate
      ? DateTime.fromISO(formData.startDate, { zone: MST_ZONE }).startOf('day').toUTC().toFormat("yyyy-MM-dd HH:mm:ssZZZ")
      : null;
    const endMST = formData.endDate
      ? DateTime.fromISO(formData.endDate, { zone: MST_ZONE }).startOf('day').toUTC().toFormat("yyyy-MM-dd HH:mm:ssZZZ")
      : null;

    const payload = {
      id: formData.id,
      name: "banner",
      on: formData.isActive,
      startDate: startMST,
      endDate: endMST,
      bannerMessage: formData.bannerMessage,
    };

    Fetch.Build("/toggle", dispatch)
      .withPayload(payload)
      .success(() => {
        history.push("/");
        window.location.reload();
      })
      .error((error: any) => {
        console.log(error);
      })
      .post();
  };

  return (
    <S.PageWithNavigationContainer>
      <BackNavigationHeader
        handleBackNavigation={() => history.push("/Admin")}
        leftContent={locale("admin.options.bannerManagement")}
      />

      <div className="banner-container">
        <div className="banner-header">Create / Edit Banner Message</div>

        <div className="banner-row">
          <div className="banner-label">Banner Visibility</div>

          <div className="banner-field">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
            />

            <span className="checkbox-text">Display this banner message</span>
          </div>
        </div>

        <div className="banner-row">
          <div className="banner-label">Start Date</div>

          <div className="banner-field">
            <input
              type="date"
              className="date-input"
              name="startDate"
              value={formData.startDate}
              min={today}
              onChange={(e) => {
                const value = e.target?.value || "";
                setFormData((prev) => ({
                  ...prev,
                  startDate: value,
                  endDate: prev.endDate && prev.endDate < value ? value : prev.endDate,
                }));
              }}
            />
          </div>
        </div>

        <div className="banner-row">
          <div className="banner-label">End Date</div>

          <div className="banner-field">
            <input
              type="date"
              className="date-input"
              name="endDate"
              value={formData.endDate}
              min={formData.startDate > today ? formData.startDate : today}
              onChange={(e) => {
                const value = e.target?.value || "";
                setFormData((prev) => ({
                  ...prev,
                  endDate: value,
                }));
              }}
            />
          </div>
        </div>

        <div className="banner-row">
          <div className="banner-label">Banner Message</div>

          <div className="banner-field">
            <textarea
              className="banner-textarea"
              name="bannerMessage"
              value={formData.bannerMessage}
              onChange={handleChange}
            />

           
          </div>
        </div>

        <div className="button-container">
          <button className="clear-button" onClick={handleClear}>
            Clear
          </button>

          <button
            className="save-button"
            onClick={handleSave}
            disabled={loading}
          >
            Save
          </button>
        </div>
      </div>
    </S.PageWithNavigationContainer>
  );
};

export default BannerManagement;
